import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import BottomNavigation from '@/components/BottomNavigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { isRideDriverEmail } from '@/config/features';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  User, 
  Mail, 
  Briefcase, 
  Truck, 
  History, 
  Heart, 
  Edit, 
  Globe, 
  HelpCircle,
  ChevronRight,
  Store,
  RefreshCw,
  ArrowLeft,
  DollarSign,
  CheckCircle,
  BadgeCheck,
  Repeat,
  Gift
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import HelpSupportDialog from '@/components/HelpSupportDialog';
import { NotificationManager } from '@/components/NotificationManager';

interface Profile {
  id: string;
  email: string;
  full_name: string;
  role: 'customer' | 'vendor' | 'driver' | 'admin';
  available_earnings: number;
  is_verified: boolean;
}

interface SupportSettings {
  phone: string;
  email: string;
  hours: string;
}

const SettingsPage = () => {
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [supportSettings, setSupportSettings] = useState<SupportSettings>({
    phone: '+2202868507',
    email: 'gambgohelp@gmail.com',
    hours: 'Mon-Fri 9AM-6PM, Sat 10AM-4PM'
  });
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const { toast } = useToast();

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
  ];

  useEffect(() => {
    fetchProfile();
    fetchSupportSettings();
  }, [user?.id]);

  const fetchSupportSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', ['support_phone', 'support_email', 'support_hours']);

      if (error) {
        console.error('Error fetching support settings:', error);
        return;
      }

      const settings = data.reduce((acc, item) => {
        let value;
        try {
          value = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
        } catch {
          value = item.value;
        }
        
        switch (item.key) {
          case 'support_phone':
            acc.phone = value;
            break;
          case 'support_email':
            acc.email = value;
            break;
          case 'support_hours':
            acc.hours = value;
            break;
        }
        return acc;
      }, { phone: '+2202554992', email: 'gambgohelp@gmail.com', hours: 'Mon-Fri 9AM-6PM, Sat 10AM-4PM' });

      setSupportSettings(settings);
    } catch (error) {
      console.error('Error parsing support settings:', error);
    }
  };

  const fetchProfile = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, role, available_earnings, is_verified')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        if (error.code === 'PGRST116') {
          const defaultProfile: Profile = {
            id: user.id,
            email: user.email || '',
            full_name: user.user_metadata?.full_name || '',
            role: 'customer' as const,
            available_earnings: 0,
            is_verified: false
          };
          setProfile(defaultProfile);
        }
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshProfile = async () => {
    setRefreshing(true);
    await fetchProfile();
    setRefreshing(false);
    toast({
      title: "Profile refreshed",
      description: "Your profile information has been updated.",
    });
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'vendor':
        return t('business_owner');
      case 'driver':
        return t('driver');
      case 'admin':
        return t('admin');
      default:
        return t('customer');
    }
  };

  const ProfileSection = ({ icon: Icon, label, value }: { icon: any, label: string, value: string }) => (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <Icon className="h-5 w-5 text-gray-600" />
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );

  const MenuButton = ({ icon: Icon, label, onClick, showChevron = true }: { 
    icon: any, 
    label: string, 
    onClick: () => void,
    showChevron?: boolean
  }) => (
    <Button
      variant="ghost"
      className="w-full justify-between p-4 h-auto"
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-gray-600" />
        <span>{label}</span>
      </div>
      {showChevron && <ChevronRight className="h-5 w-5 text-gray-400" />}
    </Button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Settings</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefreshProfile}
            disabled={refreshing}
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Profile Info */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-center mb-4">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center">
                <User className="h-10 w-10 text-white" />
              </div>
            </div>
            
            <ProfileSection 
              icon={User} 
              label={t('name')} 
              value={profile?.full_name || 'Not set'} 
            />
            <ProfileSection 
              icon={Mail} 
              label={t('email')} 
              value={profile?.email || user?.email || 'Not set'} 
            />
            <ProfileSection 
              icon={Briefcase} 
              label={t('role')} 
              value={getRoleDisplay(profile?.role || 'customer')} 
            />
          </CardContent>
        </Card>

        {/* Join GAMBGO Section - Only show for customers */}
        {profile?.role === 'customer' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('join_gambgo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <MenuButton
                icon={Briefcase}
                label={t('become_vendor')}
                onClick={() => navigate('/vendor-onboarding')}
              />
              <MenuButton
                icon={Truck}
                label={t('become_driver')}
                onClick={() => navigate('/driver-application')}
              />
            </CardContent>
          </Card>
        )}

        {/* Business Portal - Only show for vendors */}
        {profile?.role === 'vendor' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('business_portal')}</CardTitle>
            </CardHeader>
            <CardContent>
              <MenuButton
                icon={Store}
                label={t('manage_business')}
                onClick={() => navigate('/business-dashboard')}
              />
            </CardContent>
          </Card>
        )}

        {/* Driver Portal - Only show for drivers */}
        {profile?.role === 'driver' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('driver_portal')}</CardTitle>
            </CardHeader>
            <CardContent>
              <MenuButton
                icon={Truck}
                label={t('drive')}
                onClick={() => {
                  // isRideDriverEmail imported at top
                  if (isRideDriverEmail(user?.email)) {
                    navigate('/ride-driver-dashboard');
                  } else {
                    navigate('/driver-dashboard');
                  }
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* General Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('general')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <MenuButton
              icon={History}
              label={t('order_history')}
              onClick={() => navigate('/order-history')}
            />
            <MenuButton
              icon={Heart}
              label={t('favorites')}
              onClick={() => navigate('/favorites')}
            />
            <MenuButton
              icon={Edit}
              label={t('edit_profile')}
              onClick={() => navigate('/edit-profile')}
            />
            
            {/* Earnings Section - Only for drivers and customers */}
            {(profile?.role === 'driver' || profile?.role === 'customer') && (
              <MenuButton
                icon={DollarSign}
                label="Earnings"
                onClick={() => navigate('/earnings')}
              />
            )}
            
            {/* Get Verified Section - Only for drivers and customers who aren't verified */}
            {!profile?.is_verified && (profile?.role === 'driver' || profile?.role === 'customer') && (
              <MenuButton
                icon={BadgeCheck}
                label="Get Verified"
                onClick={() => navigate('/get-verified')}
              />
            )}

            {/* Swap Requests - Only for customers */}
            {profile?.role === 'customer' && (
              <MenuButton
                icon={Repeat}
                label="Swap Requests"
                onClick={() => navigate('/swap-requests')}
              />
            )}

            {/* Referrals - for all users */}
            <MenuButton
              icon={Gift}
              label="Referrals"
              onClick={() => navigate('/referral')}
            />
          </CardContent>
        </Card>

        {/* App Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('app_settings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-2">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-gray-600" />
                <span>{t('language')}</span>
              </div>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <div className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <MenuButton
              icon={HelpCircle}
              label={t('help_support')}
              onClick={() => setHelpDialogOpen(true)}
            />
          </CardContent>
        </Card>

        <NotificationManager />
        <Button
          variant="outline"
          className="w-full text-red-600 border-red-600 hover:bg-red-50"
          onClick={signOut}
        >
          {t('sign_out')}
        </Button>

        {/* Version Number */}
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">GAMBGO v1.0.0</p>
        </div>
      </div>

      <HelpSupportDialog
        open={helpDialogOpen}
        onOpenChange={setHelpDialogOpen}
        supportPhone={supportSettings.phone}
        supportEmail={supportSettings.email}
        supportHours={supportSettings.hours}
      />

      <BottomNavigation />
    </div>
  );
};

export default SettingsPage;